import { z } from 'zod';

/**
 * Which basemap the map is drawn from (docs/04-architecture.md 4.4).
 *
 * Same shape as `PLACES_PROVIDER`: one environment variable, one switch, so a
 * provider can be swapped per environment without a code change — and so that
 * P0 can put several side by side, which is what Q-1 needs
 * (docs/06-roadmap.md P0, OQ-05).
 *
 * - `protomaps-remote` — Protomaps' own daily planet build, read straight from
 *   the browser over HTTP range requests. This is the subject of Q-1: OSM data,
 *   Protomaps cartography. It needs nothing uploaded anywhere, which is why the
 *   visual comparison does not wait on the R2 upload.
 * - `protomaps-r2` — the same archive from our own R2 bucket, through the
 *   Worker. Same pixels, the production path: what P1 would actually run
 *   (docs/05-map-and-places.md 5.4), and the only one of the two whose latency
 *   and cost mean anything.
 * - `gsi` — 国土地理院 (Geospatial Information Authority of Japan) raster tiles.
 *   Not in 05.4; added as the non-commercial fallback if OSM fails Q-1, and as
 *   a yardstick for what Japanese cartography looks like when it is made for
 *   Japan (OQ-31).
 * - `demo` — MapLibre's demo tiles. Country outlines only, so it cannot answer
 *   anything about Japanese cities; it is here as a connectivity check, and
 *   because 05.4 named it as the P0 option.
 * - `none` — no map. Valid for an environment that has no map screen yet.
 */
export const mapTileProviderSchema = z.enum(['protomaps-remote', 'protomaps-r2', 'gsi', 'demo']);
export type MapTileProvider = z.infer<typeof mapTileProviderSchema>;

export const mapTileProviderSettingSchema = z.union([mapTileProviderSchema, z.literal('none')]);
export type MapTileProviderSetting = z.infer<typeof mapTileProviderSettingSchema>;

/**
 * Payload of `GET /api/v1/map/style.json`.
 *
 * `attribution` is a sibling of `style` rather than only a field inside the
 * style's sources, because the UI has to display it itself (CLAUDE.md
 * invariant 5 / R-12). Relying on MapLibre's own attribution control would make
 * the obligation depend on a control that can be switched off in one line.
 *
 * The brand that `@mapshare/tiles` puts on a checked style does NOT survive
 * this JSON — symbols never do, the same lesson `ConfirmedPlace` produced
 * (docs/08-open-questions.md 8.3). So the client re-establishes it by passing
 * the parsed payload back through `attributedStyle()`, and an empty
 * attribution fails on both sides rather than only on the server.
 */
export const attributionPartSchema = z.object({
  text: z.string().min(1),
  /** Absolute https URL, or absent for plain text such as `©`. */
  url: z.url().optional(),
});
export type AttributionPartPayload = z.infer<typeof attributionPartSchema>;

export const mapStyleResponseSchema = z.object({
  provider: mapTileProviderSchema,
  /**
   * What the UI renders, part by part. This is the field that matters: the
   * map component takes these, so a map cannot be drawn from a payload that
   * has none.
   */
  attributionParts: z.array(attributionPartSchema).min(1),
  /** The same thing flattened, for logs and for anything that wants one string. */
  attribution: z.string().min(1),
  /** Where the attribution links to, when the provider gives one. */
  attributionUrl: z.url().nullable(),
  /** Human-readable name of the provider, for the provider switch in the UI. */
  label: z.string().min(1),
  /** A MapLibre style document. Kept loose here; `@mapshare/tiles` owns its shape. */
  style: z.record(z.string(), z.unknown()),
  /** What `MAP_TILE_PROVIDER` is set to, which a `?provider=` override hides. */
  configured: mapTileProviderSettingSchema,
});

export type MapStyleResponse = z.infer<typeof mapStyleResponseSchema>;
